import { useEffect, useState } from "react";
import { Box, Group, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useGo } from "../components/ConsoleShell";
import { JsonPane, Panel, Prompt, Screen, VfToggle } from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { isValidFriendlyName } from "../lib/namespace";
import { jsonPretty } from "../lib/vf";
import css from "./screens.module.css";

const METRICS = ["cosine", "l2", "ip"];

export default function CreateNamespace() {
  const go = useGo();
  const workspaceId = useWorkspaceStore((s) => s.currentId) ?? "workspace";
  const profiles = useConsoleStore((s) => s.profiles);
  const loadProfiles = useConsoleStore((s) => s.loadProfiles);
  const createNamespace = useConsoleStore((s) => s.createNamespace);

  const [name, setName] = useState("");
  // No hardcoded default: the catalog is whatever THIS deployment registered,
  // so seeding a name the server does not have leaves the <select> matching no
  // <option> and rendering blank. Empty until the catalog answers.
  const [profile, setProfile] = useState("");
  const [dims, setDims] = useState(0);
  const [metric, setMetric] = useState("cosine");
  const [bm25, setBm25] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (profiles.length === 0) void loadProfiles();
  }, [profiles.length, loadProfiles]);

  // Adopt the server's default profile once the catalog lands — and re-adopt if
  // the current selection is not in this deployment's catalog at all.
  useEffect(() => {
    if (profiles.length === 0) return;
    if (profiles.some((p) => p.name === profile)) return;
    setProfile((profiles.find((p) => p.isDefault) ?? profiles[0]).name);
  }, [profiles, profile]);

  // Dimensions are not a free choice — they follow the selected catalog profile.
  useEffect(() => {
    const p = profiles.find((x) => x.name === profile);
    if (p) setDims(p.dims);
  }, [profile, profiles]);

  const trimmed = name.trim();
  // A namespace cannot be created against a profile this deployment does not
  // have, so the catalog must have answered before submit is live.
  const valid = trimmed.length > 0 && isValidFriendlyName(trimmed) && profile !== "";

  const requestBody = {
    dimensions: dims,
    distance_metric: metric,
    embedder_profile: profile,
    ...(bm25 ? { bm25_fields: [{ field: "content", boost: 1 }] } : {}),
  };

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await createNamespace({ name: trimmed, profile, dims, metric, bm25 });
      notifications.show({ message: `created ${workspaceId}/${trimmed}`, color: "green" });
      go("/namespaces");
    } catch (e) {
      notifications.show({
        message: e instanceof Error ? e.message : String(e),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <Prompt command="vf namespaces create" />

      <Box className={css.split2wide} style={{ maxWidth: 1000 }}>
        <Box p={18} style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}>
          <div className={css.fieldLabel}>// namespace name</div>
          <Box className={css.nsPrefixInput} mb={16}>
            <span className={css.nsPrefix}>{workspaceId}/</span>
            <input
              className={css.nsInput}
              value={name}
              placeholder="products"
              aria-label="namespace name"
              onChange={(e) => setName(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void submit();
              }}
            />
          </Box>

          <div className={css.fieldLabel}>
            // embedder_profile <span style={{ color: "var(--vf-dim2)" }}>(catalog)</span>
          </div>
          <select
            className={css.nativeSelect}
            style={{ marginBottom: 16 }}
            value={profile}
            aria-label="embedder profile"
            onChange={(e) => setProfile(e.currentTarget.value)}
          >
            {profiles.length === 0 && <option value="">loading catalog…</option>}
            {profiles.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name} · {p.dims}d
              </option>
            ))}
          </select>

          <Group gap={20} mb={16} align="flex-start" wrap="nowrap">
            <Box style={{ flex: 1 }}>
              <div className={css.fieldLabel}>// dimensions</div>
              <div className={css.dimsBox}>
                {dims || "—"} <span style={{ color: "var(--vf-dim2)" }}>· auto</span>
              </div>
            </Box>
            <Box>
              <div className={css.fieldLabel}>// bm25</div>
              <Box py={10}>
                <VfToggle checked={bm25} onChange={setBm25} aria-label="BM25 enabled" />
              </Box>
            </Box>
          </Group>

          <div className={css.fieldLabel}>// distance_metric</div>
          <Group gap={8} mb={22}>
            {METRICS.map((m) => (
              <button
                key={m}
                type="button"
                className={`${css.metricPill} ${m === metric ? css.metricPillActive : ""}`}
                onClick={() => setMetric(m)}
              >
                {m}
              </button>
            ))}
          </Group>

          <Group gap={10} wrap="nowrap">
            {valid ? (
              <button type="button" className={css.primaryWide} onClick={() => void submit()}>
                ▸ create namespace
              </button>
            ) : (
              <button type="button" className={css.primaryDisabled} disabled>
                ▸ {trimmed && !valid ? "invalid name" : "name required"}
              </button>
            )}
            <button type="button" className={css.cancelBtn} onClick={() => go("/namespaces")}>
              cancel
            </button>
          </Group>
        </Box>

        <Panel
          accentTitle
          title={`REQUEST — POST /v1/namespaces/${workspaceId}/${trimmed || "…"}`}
        >
          <JsonPane value={jsonPretty(requestBody)} />
          <Box
            p="12px 14px"
            style={{ borderTop: "1px solid var(--vf-border)" }}
          >
            <Text fz="12px" c="var(--vf-dim)" lh={1.6}>
              // config is <span style={{ color: "var(--vf-sec)" }}>immutable</span> after creation —
              dims, metric &amp; embedder need a rebuild to change.
            </Text>
          </Box>
        </Panel>
      </Box>
    </Screen>
  );
}
